import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import HttpManager from "../../../../services/HttpManager";
import { EnrollmentUpdateRow, UPDATE_TYPE, UpdateListDTO, UpdateTray } from "../../../../models/EnrollmentUpdateDTO";
import { Multi } from "../enrollmentRequests/filtrosMatriculas";
import S from "./strings";
import { TabProps, TYPE_LABELS, dinero, errorText, fechaCorta, juntar, lineaEnOro, tipoDe } from "./helpers";
import { ConfirmarAplicar, Fallo, PersonaConfirmar, PromptModal, UpdateDetail } from "./piezas";
import { etiquetaDe, horarioLeido } from "../../../../utils/etiquetaHorario";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { primeraMayuscula, textoPropio } from "../../../../utils/textoPropio";
import { useCapacidades } from "./apiBase";
import T from "./textosBase";
import "./porAplicar.css";

const POR_PAGINA = 100;

type Tray = Extract<UpdateTray, "pending" | "returned" | "mine">;

/*
	Lo que pide una decisión de Caja (clases que repite, dinero pagado que no se cursará, pagos por recorrer) se revisa abierto; lo demás
	se aplica de un clic (con confirmación). Caso #536 Dinora (31-oct): pagó Noviembre y la lista decía «Sin movimiento». El servidor también
	lo exige.
*/
const necesitaRevision = (r: EnrollmentUpdateRow): boolean => {
	const p = r.preview;
	const recorrerSinDecidir = (p?.recorrer?.sugerido || 0) !== 0 && (r.options?.recorrer === undefined || r.options?.recorrer === null);
	return r.typeId === null || r.typeId === UPDATE_TYPE.COURSE_CHANGE || r.typeId === UPDATE_TYPE.RETIRED
		|| (p?.sessionsMore || 0) > 0 || (p?.paidNotCoursed || 0) > 0 || (p?.paidNotCoursedAfter || 0) > 0 || !!p?.mueveDinero || recorrerSinDecidir;
};

/** Un cierre / fusión junto (tarjeta de grupo, como la Matrícula Grupal) o actualizaciones sueltas seguidas (una tabla). */
interface Tramo { key: string; base: string; lote: boolean; rows: EnrollmentUpdateRow[]; }

const cursoDe = (r: EnrollmentUpdateRow): string => cursoCorto(r.courseName) || r.courseName || "";
/** Curso DEL HORARIO, periodo y horario: «Maquillaje, Noviembre 2026, Domingos 10:00 am - 1:00 pm». */
const horarioCompleto = (s: EnrollmentUpdateRow["fromSchedule"]): string =>
	s ? [cursoCorto(s.courseName) || s.courseName || "", (s.periodName || "").trim(), horarioLeido(s.name)].filter(Boolean).join(", ") : "";

const PorAplicarTab: React.FC<TabProps & { initialId?: number }> = ({ campusId, countryId, movil, canApplyNow, notify, changed, initialId }) => {
	const http = HttpManager.getInstance();
	const [tray, setTray] = useState<Tray>("pending");
	const [rows, setRows] = useState<EnrollmentUpdateRow[] | null>(null);
	const [counts, setCounts] = useState<{ pending: number; returned: number }>({ pending: 0, returned: 0 });
	const [facetas, setFacetas] = useState<UpdateListDTO["facets"] | null>(null);
	const [open, setOpen] = useState<number | null>(initialId ?? null);
	// Caja / Académico salen de las capacidades (antes: `_options` de la primera fila, la consulta más cara del módulo).
	const perm = useCapacidades(campusId);
	const [total, setTotal] = useState<number>(0);
	const [paginas, setPaginas] = useState<number>(1);
	const [masBusy, setMasBusy] = useState<boolean>(false);
	const [fallo, setFallo] = useState<string | null>(null);
	const [rejecting, setRejecting] = useState<EnrollmentUpdateRow | null>(null);
	// Nada se aplica sin ver antes, por persona, qué va a pasar (también «Aplicar N»).
	const [confirmando, setConfirmando] = useState<EnrollmentUpdateRow[] | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	// Filtros en el servidor (revisión 1-nov: antes filtraban solo la página cargada)
	const [tipo, setTipo] = useState<number[]>([]);
	const [pidio, setPidio] = useState<number[]>([]);
	const [horario, setHorario] = useState<number[]>([]);
	const [tick, setTick] = useState<number>(0);

	// Lotes juntos, en el orden en que llegaron.
	const ordenar = (lista: EnrollmentUpdateRow[]) => [...lista].sort((a, b) => (a.batchId || 0) - (b.batchId || 0) || a.id - b.id);
	const filtros = () => ({ typeIds: tipo.join(","), requesterIds: pidio.join(","), scheduleIds: horario.join(",") });

	// Cada carga lleva su número: un «Cargar Más» que llega después de cambiar de bandeja o de filtro se descarta.
	const carga = useRef<number>(0);
	useEffect(() => {
		let vigente = true;
		carga.current++;
		setRows(null); setFallo(null);
		const pedir = (pagina: number) => http.enrollmentUpdatesService.list(campusId, tray, { perPage: POR_PAGINA, page: pagina, ...filtros() });
		Promise.all(Array.from({ length: paginas }, (_, i) => pedir(i + 1))).then((rs) => {
			if (!vigente) { return; }
			const vistas = new Set<number>();
			const todas = rs.flatMap((r) => r.data.data).filter((x) => (vistas.has(x.id) ? false : (vistas.add(x.id), true)));
			setRows(ordenar(todas));
			setTotal(rs[0].data.total || todas.length);
			if (rs[0].data.facets) { setFacetas(rs[0].data.facets); }
			if (rs[0].data.counts) { setCounts({ pending: rs[0].data.counts.pending, returned: rs[0].data.counts.returned }); }
		}).catch((e) => vigente && setFallo(errorText(e)));
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, tray, tick, tipo, pidio, horario]);

	const cargarMas = () => {
		if (!rows || masBusy) { return; }
		setMasBusy(true);
		const siguiente = paginas + 1;
		const esta = carga.current;
		http.enrollmentUpdatesService.list(campusId, tray, { perPage: POR_PAGINA, page: siguiente, ...filtros() }).then((r) => {
			if (esta !== carga.current) { return; }
			setPaginas(siguiente);
			setRows((antes) => {
				const ids = new Set((antes || []).map((x) => x.id));
				return ordenar([...(antes || []), ...r.data.data.filter((x) => !ids.has(x.id))]);
			});
			setTotal(r.data.total || 0);
		}).catch((e) => notify(errorText(e), false)).finally(() => setMasBusy(false));
	};

	const tramos: Tramo[] = useMemo(() => {
		const out: Tramo[] = [];
		(rows || []).forEach((r) => {
			const lote = !!r.batchId && (r.batchKind === "close" || r.batchKind === "merge" || r.batchKind === "roster");
			const key = lote ? "b" + r.batchId : "sueltas";
			const ultimo = out[out.length - 1];
			if (ultimo && ultimo.base === key) { ultimo.rows.push(r); } else { out.push({ key: key + "_" + out.length, base: key, lote, rows: [r] }); }
		});
		return out;
	}, [rows]);

	const recargar = (msg?: string) => { if (msg) { notify(msg); } setOpen(null); setTick((t) => t + 1); changed(); };
	const puedeAplicar = (r: EnrollmentUpdateRow) => canApplyNow && (perm.isCaja || (perm.isAcademic && !r.needsCashier));
	const simplesDe = (rs: EnrollmentUpdateRow[]) => rs.filter((r) => r.statusId === 1 && !necesitaRevision(r) && puedeAplicar(r));

	/** Aplica una por una, en orden; si una falla se detiene ahí y se dice cuál (las demás siguen Por Aplicar). */
	const aplicando = useRef<boolean>(false);
	const ejecutar = async (lista: EnrollmentUpdateRow[]) => {
		if (aplicando.current) { return; }
		aplicando.current = true;
		setBusy(true);
		let hechas = 0;
		for (const r of lista) {
			try { await http.enrollmentUpdatesService.apply(campusId, r.id, {}); hechas++; }
			catch (e) { notify(`${r.studentName}: ${errorText(e)}`, false); break; }
		}
		setBusy(false);
		aplicando.current = false;
		setConfirmando(null);
		if (hechas) { setTick((t) => t + 1); changed(); setOpen(null); if (hechas === lista.length) { notify(S.appliedN(hechas)); } }
	};
	const rechazar = (motivo: string) => {
		if (!rejecting) { return; }
		setBusy(true);
		http.enrollmentUpdatesService.discard(campusId, rejecting.id, motivo)
			.then(() => { setRejecting(null); recargar(S.rejected); })
			.catch((e) => notify(errorText(e), false)).finally(() => setBusy(false));
	};

	/** «Qué Va a Pasar» por persona: lo arma el servidor; sin tipo todavía, se dice que falta elegir. Si no llegó, lo de antes (dinero). */
	const lineasDe = (r: EnrollmentUpdateRow): string[] => {
		if (r.typeId === null) { return [S.sinTipo]; }
		const p = r.preview;
		if (p?.efectos?.length) { return p.efectos; }
		const partes: string[] = [];
		if (p?.overdue) { partes.push(S.payOverdue(dinero(p.overdue, countryId))); }
		if (p?.paidNotCoursedAfter || p?.paidNotCoursed) { partes.push(`${S.paidNotCoursed} ${dinero(p.paidNotCoursedAfter || p.paidNotCoursed, countryId)}`); }
		if (p?.sessionsMore) { partes.push(S.moreSessions(p.sessionsMore)); }
		return partes.length ? partes : [S.col.moneyOk];
	};
	const efectos = (r: EnrollmentUpdateRow) => (
		<div className="wau-efectos">{lineasDe(r).map((l, i) => <div key={"e" + i} className={lineaEnOro(l) ? "waOroTexto" : ""}>{l}</div>)}</div>
	);
	const persona = (r: EnrollmentUpdateRow): PersonaConfirmar => ({ nombre: r.studentName, curso: cursoDe(r), tipo: tipoDe(r), lineas: lineasDe(r) });
	/** La nota propia en un renglón (completa al pasar el cursor); el motivo del lote ya va en su cabecera. */
	const nota = (r: EnrollmentUpdateRow) => {
		const texto = primeraMayuscula((r.noteOwn ?? r.note) || "");
		return (
			<>
				{texto ? <div className="wau-sec small wau-una-linea" title={texto}>{texto}</div> : null}
				{r.statusId === 2 && r.returnReason ? <div className="waOroTexto small">{S.fixReason}: {r.returnReason}</div> : null}
			</>
		);
	};
	const cambio = (r: EnrollmentUpdateRow, lote: boolean) => {
		const destino = r.toCourse ? `${r.toCourse.name}${r.toSchedule ? ", " + etiquetaDe(r.toSchedule) : ""}` : etiquetaDe(r.toSchedule);
		return (
			<>
				<Badge bg={r.typeId === null ? "oro" : "info"} style={{ fontWeight: 500 }}>{r.typeId === null ? S.choose : tipoDe(r)}</Badge>
				{destino && !(lote && r.batchKind === "merge") ? <div className="wau-sec small" style={{ marginTop: 2 }}>→ {destino.replace(" — ", ", ")}</div> : null}
				{nota(r)}
			</>
		);
	};
	const botones = (r: EnrollmentUpdateRow) => (
		<div className="d-flex gap-1 justify-content-end flex-nowrap">
			{tray === "pending" && puedeAplicar(r) && !necesitaRevision(r)
				? <button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy} onClick={() => setConfirmando([r])}>{S.apply}</button>
				: <button type="button" className="btn btn-primary btn-sm rounded-pill" aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? null : r.id)}>{tray === "pending" && puedeAplicar(r) ? S.review : S.open}</button>}
			<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setRejecting(r)}>{S.reject}</button>
		</div>
	);
	const detalleAbierto = (r: EnrollmentUpdateRow) => open === r.id
		? <UpdateDetail campusId={campusId} countryId={countryId} id={r.id} notifyError={(m) => notify(m, false)} onChanged={(m) => recargar(m)} /> : null;

	/** Cabecera del lote: «Cierre de Horario de {curso del horario}, {periodo}, {horario}» + motivo y quién pidió en una línea apagada. */
	const cabeza = (t: Tramo) => {
		const r = t.rows[0];
		const simples = tray === "pending" ? simplesDe(t.rows) : [];
		const origen = horarioCompleto(r.fromSchedule) || juntar(t.rows.map(cursoDe));
		const titulo = r.batchKind === "close" ? S.cierreDe(origen) : r.batchKind === "merge" ? S.fusionDe(origen) : (r.batchLabel && !/^Lote de \d+/.test(r.batchLabel) ? textoPropio(r.batchLabel) : origen);
		const icono = r.batchKind === "close" ? solid("door-closed") : r.batchKind === "merge" ? solid("code-merge") : solid("users");
		const destino = r.batchKind === "merge" && t.rows.every((x) => x.toSchedule?.id === r.toSchedule?.id) && r.toSchedule ? S.pasanA(etiquetaDe(r.toSchedule).replace(" — ", ", ")) : null;
		const detalle = [destino, r.batchReason ? `${S.motivo}: ${primeraMayuscula(r.batchReason)}` : null, `${r.requestedBy || "—"}, ${fechaCorta(r.requestedAt)}`].filter(Boolean).join(". ");
		return (
			<div className="waGrupoCabeza d-flex justify-content-between align-items-start gap-2 flex-wrap">
				<div style={{ minWidth: 0 }}>
					<span className="waGrupoTitulo"><FontAwesomeIcon icon={icono} /><b>{titulo}</b><span>{t.rows.length} {t.rows.length === 1 ? "alumna" : "alumnas"}</span></span>
					<div className="wau-sec small" style={{ marginTop: 2 }}>{detalle}</div>
				</div>
				{simples.length > 1 ? <button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy} onClick={() => setConfirmando(simples)}>{S.applyN(simples.length)}</button> : null}
			</div>
		);
	};

	const tabla = (t: Tramo) => movil ? (
		<div className="wau-pila" style={{ gap: 8 }}>
			{t.rows.map((r) => (
				<div className="wau-tarjeta" key={"u" + r.id} style={{ marginBottom: 0 }}>
					<div className="d-flex justify-content-between gap-2 align-items-start"><b>{r.studentName}</b>{botones(r)}</div>
					{cursoDe(r) ? <div className="wau-curso">{cursoDe(r)}</div> : null}
					<div style={{ margin: "4px 0" }}>{cambio(r, t.lote)}</div>
					{efectos(r)}
					{detalleAbierto(r)}
				</div>
			))}
		</div>
	) : (
		<div className="wau-tabla waScrollX" style={{ background: "var(--wa-superficie)", borderRadius: 8 }}>
			<table className="wau-tabla-aplicar">
				<colgroup><col style={{ width: "22%" }} /><col style={{ width: "24%" }} /><col /><col style={{ width: 190 }} /></colgroup>
				<thead><tr><th>{S.col.student}</th><th>{S.col.change}</th><th>{S.col.whatHappens}</th><th /></tr></thead>
				<tbody>
					{t.rows.map((r) => (
						<React.Fragment key={"u" + r.id}>
							<tr className={open === r.id ? "sel" : ""}>
								<td><b>{r.studentName}</b>{cursoDe(r) ? <div className="wau-curso">{cursoDe(r)}</div> : null}</td>
								<td>{cambio(r, t.lote)}</td>
								<td>{efectos(r)}</td>
								<td className="der">{botones(r)}</td>
							</tr>
							{open === r.id ? <tr><td colSpan={4} style={{ padding: 0 }}>{detalleAbierto(r)}</td></tr> : null}
						</React.Fragment>
					))}
				</tbody>
			</table>
		</div>
	);

	const trays: { key: Tray; label: string; n?: number }[] = [
		{ key: "pending", label: S.trays.pending, n: counts.pending }, { key: "returned", label: S.trays.returned, n: counts.returned }, { key: "mine", label: S.trays.mine }
	];
	const hayFiltros = tipo.length + pidio.length + horario.length > 0;

	return (
		<div className="wau-pila">
			<div className="waFmBarra">
				<div className="waFmSegs">
					{trays.map((t) => (
						<button key={t.key} type="button" className={"waFmSeg" + (tray === t.key ? " activo" : "")}
							onClick={() => { setTray(t.key); setPaginas(1); setOpen(null); setTipo([]); setPidio([]); setHorario([]); }}>
							{t.label}{t.n ? <span className="waTabCuenta" style={{ marginLeft: 6 }}>{t.n}</span> : null}
						</button>
					))}
				</div>
				<div className="waFmBarraListas">
					<Multi titulo={S.filterType} opciones={(facetas?.types || []).map((id) => ({ id, name: TYPE_LABELS[id] || String(id) }))} elegidos={tipo} onChange={(v) => { setPaginas(1); setTipo(v); }} />
					<Multi titulo={S.filterRequester} buscar opciones={(facetas?.requesters || []).map((x) => ({ id: x.id, name: x.name }))} elegidos={pidio} onChange={(v) => { setPaginas(1); setPidio(v); }} />
					<Multi titulo={S.filterSchedule} buscar opciones={(facetas?.schedules || []).map((x) => ({ id: x.id, name: etiquetaDe(x).replace(" — ", ", ") }))} elegidos={horario} onChange={(v) => { setPaginas(1); setHorario(v); }} />
					{hayFiltros ? <button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={() => { setPaginas(1); setTipo([]); setPidio([]); setHorario([]); }}>{S.removeFilters}</button> : null}
				</div>
			</div>

			{/* El enlace (?actualizacion=) apunta a una que no está en esta bandeja o en lo cargado: se abre igual, arriba. */}
			{!fallo && rows !== null && open !== null && open === initialId && !rows.some((r) => r.id === open)
				? <UpdateDetail campusId={campusId} countryId={countryId} id={open} notifyError={(m) => notify(m, false)} onChanged={(m) => recargar(m)} /> : null}
			{fallo ? <Fallo texto={fallo} onReintentar={() => setTick((t) => t + 1)} /> : rows === null ? <div className="wau-caja wau-sec">{S.loading}</div> : tramos.length === 0 ? <div className="wau-caja wau-sec">{S.empty}</div> : tramos.map((t) => (
				t.lote ? (
					<div className="waGrupoCaja" key={t.key}>
						{cabeza(t)}
						{tabla(t)}
					</div>
				) : <div key={t.key}>{tabla(t)}</div>
			))}

			{!fallo && rows && rows.length < total ? (
				<div className="wau-acciones" style={{ justifyContent: "center" }}>
					<span className="wau-sec wau-num">{T.deTotal(rows.length, total)}</span>
					<button type="button" className="btn btn-outline-secondary btn-sm" disabled={masBusy} onClick={cargarMas}>{T.cargarMas}</button>
				</div>
			) : null}
			{rejecting ? <PromptModal title={S.reject} label={S.rejectReason} confirm={S.reject} busy={busy} onCancel={() => setRejecting(null)} onConfirm={rechazar}>
				<p className="wau-sec" style={{ marginTop: 0 }}>{rejecting.studentName}, {tipoDe(rejecting)}</p>
			</PromptModal> : null}
			{confirmando ? <ConfirmarAplicar personas={confirmando.map(persona)} busy={busy} onCancel={() => setConfirmando(null)} onConfirm={() => ejecutar(confirmando)} /> : null}
		</div>
	);
};

export default PorAplicarTab;
